// // Library methods
import { useTranslation } from "react-i18next";
import React, { useContext, useEffect, useMemo, useState } from "react";

// MUI Components
import {
  Box,
  Button,
  FormControlLabel,
  FormGroup,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import DefaultModal from "../DefaultModal";
import CustomSwitch from "../CustomSwitch";

// Styles
import {
  ModalCustomConfirm,
  ModalCustomCancel,
} from "../../../styles/muiStylesHelper";

// Contexts
import { ReportEachSectionContext } from "../../../contexts/CommentContext";
import { ReportDataContext } from "../../../contexts/ReportDataContext";

// Utilities
import { conditionTitles } from "../../../enums";
import { sortByDate } from "../../../utils/dateHelper";

const AddCommentModal = ({ isOpen, handleClose, submitComment }) => {
  const { t } = useTranslation();

  // contexts
  const { refetchComment, getReportInfoData } = useContext(ReportDataContext);
  const { comments } = useContext(ReportEachSectionContext);

  const maxLength = 200;

  const enumOptions = useMemo(
    () => Object.keys(conditionTitles).map((key) => key),
    []
  );

  // sort newest comments first
  const sortedComments = useMemo(
    () => sortByDate(comments, "creationDate"),
    [comments]
  );

  const latestComment = useMemo(
    () => sortedComments?.[0] ?? null,
    [sortedComments]
  );
  const latestToggles = useMemo(
    () =>
      enumOptions.reduce(
        (acc, curr) => ({
          ...acc,
          [curr]: Boolean(
            latestComment && latestComment.conditions.includes(curr)
          ),
        }),
        {}
      ),
    [enumOptions, latestComment]
  );

  const [commentValue, setCommentValue] = useState("");
  const [toggleState, setToggleState] = useState({ ...latestToggles });

  // reset toggles every time the modal is opened
  useEffect(() => {
    if (isOpen) {
      setToggleState({ ...latestToggles });
    }
  }, [isOpen, latestToggles]);

  const shouldDisable = useMemo(
    () => !commentValue || !commentValue?.trim(),
    [commentValue]
  );

  const handleChange = (e) => {
    setCommentValue(e.target.value);
  };

  const handleToggle = (event) => {
    setToggleState({
      ...toggleState,
      [event.target.name]: event.target.checked,
    });
  };

  const resetData = () => {
    setToggleState({});
    setCommentValue("");
  };

  const handleSubmit = async () => {
    // Add a comment
    // extract conditions except the last item which means 'other condition'.
    const keys = Object.keys(toggleState);
    const conditions = keys
      .filter((key) => toggleState[key])
      .map((key) => `${key}`);

    // replace one or more newlines with a single space
    const replacedComment = commentValue?.replace(/\n+/g, " ");
    const commentData = {
      content: replacedComment,
    };

    if (conditions.length)
      commentData["conditions"] = `[${conditions.map(
        (condition) => `"` + condition + `"`
      )}]`;

    try {
      await submitComment(commentData);

      // display new comments on the page
      refetchComment();
      getReportInfoData();

      // scroll to comments
      setTimeout(() => {
        document
          ?.getElementById("comments-list")
          ?.scrollIntoView({ behavior: "smooth" });
      }, 100);

      // reset and close the modal
      resetData();
      handleClose();
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={() => {
        resetData();
        handleClose();
      }}
      title={t("add_comment")}
      maxWidth="sm"
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "20px",
          }}
        >
          <Button
            onClick={() => {
              resetData();
              handleClose();
            }}
            sx={ModalCustomCancel}
          >
            {t("button_cancel")}
          </Button>
          <Button
            variant="outlined"
            onClick={handleSubmit}
            sx={ModalCustomConfirm}
            disabled={shouldDisable}
          >
            {t("button_add")}
          </Button>
        </Grid>
      }
    >
      <Grid display="flex" flexDirection="column" p="20px" gap="20px">
        <Box>
          <TextField
            value={commentValue}
            autoFocus
            margin="dense"
            id="Input comments"
            label={t("comment_text")}
            type="text"
            fullWidth
            multiline
            minRows={4}
            maxRows={4}
            color="warning"
            inputProps={{ maxLength }}
            onChange={handleChange}
          />
          <Typography
            textAlign="right"
            color={commentValue?.length === maxLength ? "red" : "inherit"}
          >
            {`${commentValue?.length ?? 0}/${maxLength}`}
          </Typography>
        </Box>
        <FormGroup row>
          <Grid container gap="8px">
            {enumOptions.map((option) => (
              <FormControlLabel
                key={option}
                label={<Typography>{option}</Typography>}
                labelPlacement="end"
                control={
                  <CustomSwitch
                    checked={toggleState?.[option]}
                    onChange={handleToggle}
                    name={option}
                  />
                }
              />
            ))}
          </Grid>
        </FormGroup>
      </Grid>
    </DefaultModal>
  );
};

export default AddCommentModal;
